import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Usuarios = () => {
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [form] = Form.useForm();
  
  // Verificar si es admin principal (sin empresa_id)
  const isMainAdmin = user && user.rol === 'admin' && !user.empresa_id;

  useEffect(() => {
    fetchUsuarios();
    fetchPersonal();
    if (isMainAdmin) {
      fetchEmpresas();
    }
  }, [isMainAdmin]);

  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const response = await api.get('/usuarios');
      setUsuarios(response.data);
    } catch (error) {
      message.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonal = async () => {
    try {
      const response = await api.get('/personal');
      // Filtrar solo personal activo que no tenga usuario asignado
      const personalSinUsuario = response.data.filter(p => p.estado === 'activo');
      setPersonal(personalSinUsuario);
    } catch (error) {
      console.error('Error al cargar personal');
    }
  };

  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data);
    } catch (error) {
      console.error('Error al cargar empresas');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingUsuario) {
        await api.put(`/usuarios/${editingUsuario.id}`, values);
        message.success('Usuario actualizado exitosamente');
      } else {
        await api.post('/usuarios', values);
        message.success('Usuario creado exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingUsuario(null);
      fetchUsuarios();
      fetchPersonal();
    } catch (error) {
      message.error(error.response?.data?.message || 'Error al guardar usuario');
    }
  };

  const handleEdit = (record) => {
    setEditingUsuario(record);
    form.setFieldsValue({
      ...record,
      password: '', // No mostrar contraseña
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/usuarios/${id}`);
      message.success('Usuario eliminado exitosamente');
      fetchUsuarios();
      fetchPersonal();
    } catch (error) {
      message.error(error.response?.data?.message || 'Error al eliminar usuario');
    }
  };

  const getRolColor = (rol) => {
    const colores = {
      admin: 'red',
      operador: 'blue',
    };
    return colores[rol] || 'default';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Rol',
      dataIndex: 'rol',
      key: 'rol',
      render: (rol) => (
        <Tag color={getRolColor(rol)}>
          {rol ? rol.toUpperCase() : '-'}
        </Tag>
      ),
    },
    {
      title: 'Empresa',
      dataIndex: 'empresa_nombre',
      key: 'empresa',
      render: (empresa) => empresa || <Tag>Sin empresa</Tag>,
    },
    {
      title: 'Personal Asociado',
      key: 'personal',
      render: (_, record) => {
        if (record.personal_nombre) {
          return (
            <div>
              <div style={{ fontWeight: 'bold' }}>{record.personal_nombre}</div>
              <div style={{ fontSize: '12px', color: '#999' }}>
                {record.personal_documento} - {record.personal_cargo}
              </div>
            </div>
          );
        }
        return <Tag>Sin personal asignado</Tag>;
      },
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const colors = {
          activo: 'green',
          inactivo: 'red',
        };
        return <Tag color={colors[estado]}>{estado ? estado.toUpperCase() : 'ACTIVO'}</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar este usuario?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Gestión de Usuarios"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingUsuario(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Nuevo Usuario
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={usuarios}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingUsuario ? 'Editar Usuario' : 'Nuevo Usuario'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingUsuario(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="nombre"
            label="Nombre"
            rules={[{ required: true, message: 'Ingresa el nombre' }]}
          >
            <Input prefix={<UserOutlined />} />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Ingresa el email' },
              { type: 'email', message: 'Email inválido' }
            ]}
          >
            <Input type="email" />
          </Form.Item>
          <Form.Item
            name="password"
            label={editingUsuario ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
            rules={editingUsuario ? [] : [{ required: true, message: 'Ingresa la contraseña' }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="rol"
            label="Rol"
            rules={[{ required: true, message: 'Selecciona el rol' }]}
            initialValue="operador"
          >
            <Select placeholder="Selecciona el rol">
              <Select.Option value="admin">Administrador</Select.Option>
              <Select.Option value="operador">Operador</Select.Option>
            </Select>
          </Form.Item>
          {isMainAdmin && (
            <Form.Item
              name="empresa_id"
              label="Empresa"
              tooltip="Opcional: Asignar este usuario a una empresa específica. Si se deja vacío, será administrador principal."
            >
              <Select 
                placeholder="Selecciona la empresa (opcional)"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {empresas.map((e) => (
                  <Select.Option key={e.id} value={e.id}>
                    {e.nombre}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item
            name="personal_id"
            label="Personal Asociado"
            tooltip="Opcional: Asociar este usuario a un trabajador del personal"
          >
            <Select 
              placeholder="Selecciona el personal (opcional)"
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {personal.map((p) => (
                <Select.Option key={p.id} value={p.id}>
                  {p.nombre_completo} - {p.documento} ({p.cargo})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="estado"
            label="Estado"
            initialValue="activo"
          >
            <Select>
              <Select.Option value="activo">Activo</Select.Option>
              <Select.Option value="inactivo">Inactivo</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingUsuario ? 'Actualizar' : 'Crear'}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingUsuario(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Usuarios;

